import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { MapPin, Navigation, Home as HomeIcon, Crosshair, Bookmark, ChevronDown, X, Eye, EyeOff } from "lucide-react";

/**
 * Single "Where" burger-style chip that replaces From me / Explore / Trips / Set home.
 * The chip label reflects the currently-active location mode:
 *   - "Near home"    when the user has a home pin and it's the origin
 *   - "From me"      when live GPS is the origin
 *   - "Scouting …"   when an explore pin is set
 *   - "Pick a spot"  when nothing is active
 *
 * Options inside:
 *   - Use my location (activates live GPS)
 *   - Home pin (Set / Edit — depending on whether one exists)
 *   - Explore an address (opens the location search dialog)
 *   - Drop pin on map (arms scout mode)
 *   - My trips (auth only)
 *   - Clear explore (only while scouting)
 */
export default function WhereMenu({
    fromMe, scoutPin, scoutMode, showScoutMarker, homePos, geoLoading,
    onFromMe, onSetHomePin, onExploreAddress, onDropPin, onOpenTrips, onClearScout, onToggleScoutMarker,
    user,
}) {
    // Chip label + accent colour based on active mode. Mirrors the old individual chips.
    let mode = "idle";
    let label = "Pick a spot";
    let accent = "hsl(30 8% 55%)";
    if (scoutPin) { mode = "scout"; label = `Scouting ${(scoutPin.display_name || "").split(",").slice(0, 1)[0] || "…"}`; accent = "hsl(290 45% 55%)"; }
    else if (scoutMode) { mode = "arming"; label = "Tap the map…"; accent = "hsl(290 45% 55%)"; }
    else if (fromMe) { mode = "gps"; label = geoLoading ? "Locating…" : "From me"; accent = "hsl(220 55% 45%)"; }
    else if (homePos) { mode = "home"; label = "Near home"; accent = "hsl(131 22% 29%)"; }

    const isActive = mode !== "idle";
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="where-menu-toggle"
                    className={`inline-flex flex-shrink-0 items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5 border-l-4 ${
                        isActive ? "glass shadow-md text-foreground" : "bg-white/50 text-muted-foreground border border-stone-200 border-l-stone-300"
                    }`}
                    style={isActive ? { boxShadow: `0 4px 20px ${accent}33`, borderLeftColor: accent } : {}}
                    title="Where are you exploring from?"
                >
                    {mode === "gps" ? <Navigation size={14} style={{ color: accent }} />
                        : mode === "home" ? <HomeIcon size={14} style={{ color: accent }} />
                        : mode === "scout" || mode === "arming" ? <Crosshair size={14} style={{ color: accent }} />
                        : <MapPin size={14} />}
                    <span className="max-w-[120px] truncate">{label}</span>
                    <ChevronDown size={12} className="opacity-60" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64" data-testid="where-menu">
                <DropdownMenuLabel className="text-xs uppercase tracking-widest">Explore from</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    data-testid="where-option-from-me"
                    onClick={onFromMe}
                    className={fromMe ? "font-bold" : ""}
                >
                    <Navigation size={14} className="mr-2" style={{ color: fromMe ? "hsl(220 55% 45%)" : undefined }} />
                    <div>
                        <div className="font-semibold">Use my location</div>
                        <div className="text-xs text-muted-foreground">{fromMe ? "Currently active — tap to turn off" : "Live GPS, updates as you move"}</div>
                    </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                    data-testid="where-option-home"
                    onClick={onSetHomePin}
                    className={homePos ? "font-bold" : ""}
                >
                    <HomeIcon size={14} className="mr-2" style={{ color: homePos ? "hsl(131 22% 29%)" : undefined }} />
                    <div>
                        <div className="font-semibold">{homePos ? "Edit home pin" : "Set home pin"}</div>
                        <div className="text-xs text-muted-foreground">{homePos ? "Your default radius origin" : "One-time — sticks across visits"}</div>
                    </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    data-testid="where-option-explore-address"
                    onClick={onExploreAddress}
                >
                    <MapPin size={14} className="mr-2" />
                    <div>
                        <div className="font-semibold">Explore an address</div>
                        <div className="text-xs text-muted-foreground">Type a city, street or postcode</div>
                    </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                    data-testid="where-option-drop-pin"
                    onClick={onDropPin}
                >
                    <Crosshair size={14} className="mr-2" />
                    <div>
                        <div className="font-semibold">Drop pin on map</div>
                        <div className="text-xs text-muted-foreground">Tap anywhere to explore that spot</div>
                    </div>
                </DropdownMenuItem>
                {user && (
                    <DropdownMenuItem
                        data-testid="where-option-trips"
                        onClick={onOpenTrips}
                    >
                        <Bookmark size={14} className="mr-2" />
                        <div>
                            <div className="font-semibold">My saved trips</div>
                            <div className="text-xs text-muted-foreground">Jump back to a spot you saved</div>
                        </div>
                    </DropdownMenuItem>
                )}
                {scoutPin && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            data-testid="where-option-toggle-scout-marker"
                            onClick={(e) => { e.preventDefault(); onToggleScoutMarker?.(); }}
                            onSelect={(e) => e.preventDefault()}
                        >
                            {showScoutMarker ? <EyeOff size={14} className="mr-2" /> : <Eye size={14} className="mr-2" />}
                            <div className="flex-1">
                                <div className="font-bold text-sm">{showScoutMarker ? "Hide pin on map" : "Show pin on map"}</div>
                                <div className="text-xs text-muted-foreground">{showScoutMarker ? "Keep the radius ring, hide the marker" : "Make the pin visible again"}</div>
                            </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            data-testid="where-option-clear-scout"
                            onClick={onClearScout}
                            className="text-destructive focus:text-destructive"
                        >
                            <X size={14} className="mr-2" /> Clear explore pin
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
